<template>
  <div>
    <Header />
    <v-container fluid class="pa-4">
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="headline">
            <v-icon left>mdi-printer</v-icon>
            Druckerverwaltung
          </v-card-title>

          <v-card-text>
            <v-alert v-if="error" type="error" dismissible @input="error = null" class="mb-4">
              {{ error }}
            </v-alert>

            <v-alert v-if="successMessage" type="success" dismissible @input="successMessage = null" class="mb-4">
              {{ successMessage }}
            </v-alert>

            <v-data-table
              :headers="headers"
              :items="printers"
              :loading="loading"
              class="elevation-1"
              :items-per-page="10"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-toolbar-title>Packtisch-Drucker</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" @click="loadPrinters" :loading="loading">
                    <v-icon left>mdi-refresh</v-icon>
                    Aktualisieren
                  </v-btn>
                </v-toolbar>
              </template>

              <template v-slot:item.online="{ item }">
                <v-chip :color="item.online ? 'success' : 'error'" small>
                  {{ item.online ? 'Online' : 'Offline' }}
                </v-chip>
              </template>

              <template v-slot:item.updated="{ item }">
                {{ formatDate(item.updated) }}
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn icon @click="editPrinter(item)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">Drucker bearbeiten: {{ editedPrinter.name }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedPrinter.url"
                  label="Etikettendrucker URL"
                  prepend-icon="mdi-printer"
                  :rules="[rules.required, rules.url]"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="editedPrinter.a4"
                  label="A4 Drucker URL"
                  prepend-icon="mdi-file-document"
                  :rules="[rules.required, rules.url]"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="editedPrinter.wkl"
                  label="WKL Drucker URL"
                  prepend-icon="mdi-label"
                  :rules="[rules.required, rules.url]"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeEditDialog">
            Abbrechen
          </v-btn>
          <v-btn color="primary" @click="savePrinter" :loading="saving">
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </v-container>
  </div>
</template>

<script>
import Api from "@/services/Api";
import Header from "./header.vue";

export default {
  name: "PrinterManagement",

  components: {
    Header,
  },

  data() {
    return {
      printers: [],
      loading: false,
      saving: false,
      error: null,
      successMessage: null,
      editDialog: false,
      editedPrinter: {
        _id: null,
        name: "",
        url: "",
        a4: "",
        wkl: "",
      },
      headers: [
        { text: "Name", value: "name", sortable: true },
        { text: "Status", value: "online", sortable: true },
        { text: "Etikettendrucker", value: "url", sortable: false },
        { text: "A4 Drucker", value: "a4", sortable: false },
        { text: "WKL Drucker", value: "wkl", sortable: false },
        { text: "Zuletzt aktualisiert", value: "updated", sortable: true },
        { text: "Aktionen", value: "actions", sortable: false, align: "center" },
      ],
      rules: {
        required: value => !!value || 'Dieses Feld ist erforderlich',
        url: value => {
          const pattern = /^https?:\/\/.+/;
          return pattern.test(value) || 'Bitte geben Sie eine gültige URL ein';
        },
      },
    };
  },

  mounted() {
    this.loadPrinters();
  },

  methods: {
    async loadPrinters() {
      this.loading = true;
      this.error = null;

      try {
        const response = await Api().get("/printer-management/printers");

        if (response.data.success) {
          this.printers = response.data.printers;
        } else {
          this.error = "Fehler beim Laden der Drucker: " + (response.data.error || "Unbekannter Fehler");
        }
      } catch (error) {
        console.error("Error loading printers:", error);
        this.error = "Fehler beim Laden der Drucker: " + (error.response?.data?.error || error.message);
      } finally {
        this.loading = false;
      }
    },

    editPrinter(printer) {
      this.editedPrinter = {
        _id: printer._id,
        name: printer.name,
        url: printer.url || "",
        a4: printer.a4 || "",
        wkl: printer.wkl || "",
      };
      this.editDialog = true;
    },

    closeEditDialog() {
      this.editDialog = false;
      this.editedPrinter = {
        _id: null,
        name: "",
        url: "",
        a4: "",
        wkl: "",
      };
    },

    async savePrinter() {
      this.saving = true;
      this.error = null;

      try {
        const printerId = typeof this.editedPrinter._id === 'object'
          ? this.editedPrinter._id.$oid
          : this.editedPrinter._id;

        const response = await Api().put(`/printer-management/printers/${printerId}`, {
          url: this.editedPrinter.url,
          a4: this.editedPrinter.a4,
          wkl: this.editedPrinter.wkl,
        });

        if (response.data.success) {
          this.successMessage = "Druckerkonfiguration erfolgreich gespeichert!";
          this.closeEditDialog();
          await this.loadPrinters();
        } else {
          this.error = "Fehler beim Speichern: " + (response.data.error || "Unbekannter Fehler");
        }
      } catch (error) {
        console.error("Error saving printer:", error);
        this.error = "Fehler beim Speichern: " + (error.response?.data?.error || error.message);
      } finally {
        this.saving = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "-";

      const date = new Date(dateString.$date || dateString);

      if (isNaN(date.getTime())) return "-";

      return new Intl.DateTimeFormat("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
  },
};
</script>

<style scoped>
.v-data-table {
  margin-top: 20px;
}
</style>